import { useCallback, useMemo } from 'react';
import { useSaldosClientes } from './useSaldosClientes';
import { aggregateMovimientosDelDia, sumSaldosTotales } from '../utils/balanceCalculator';
import {
    buildScope,
    filterByComitentes,
    filterMapByComitentes,
    filterSnapshotByComitentes,
    scopeKey,
} from '../utils/saldosScope';
import { SNAPSHOT_TIPO } from '../services/saldosService';
import {
    BUCKET_ARS,
    BUCKET_USD,
    BUCKET_CABLE,
    MONEDA_BUCKETS,
    SNAPSHOT_SALDO_FIELD,
    SALDO_INICIAL_FIELD,
} from '../constants/monedas';

const sumSnapshotInicio = (snapshotInicial, initialSaldos, moneda) => {
    if (snapshotInicial) {
        const field = SNAPSHOT_SALDO_FIELD[moneda];
        const total = (snapshotInicial.comitentes || [])
            .reduce((acc, c) => acc + (Number(c[field]) || 0), 0);
        return { saldoInicio: total, saldoInicioEsFallback: false };
    }
    const fallbackField = SALDO_INICIAL_FIELD[moneda];
    const total = initialSaldos.reduce((acc, s) => acc + (Number(s[fallbackField]) || 0), 0);
    return { saldoInicio: total, saldoInicioEsFallback: true };
};

const collectPendientesItems = (map) => {
    const items = [];
    map.forEach(entry => {
        entry.movimientosPendientes.forEach(mov => {
            items.push({ ...mov, nroComitente: entry.nroComitente, cliente: entry.nombre });
        });
    });
    return items;
};

const topEntries = (map, sign, limit = 5) => {
    const entries = [];
    map.forEach(entry => {
        if (sign > 0 ? entry.saldoReal > 0 : entry.saldoReal < 0) entries.push(entry);
    });
    entries.sort((a, b) => sign > 0 ? b.saldoReal - a.saldoReal : a.saldoReal - b.saldoReal);
    return entries.slice(0, limit);
};

/*
 * View-model del Dashboard de Saldos: consolida por moneda los totales,
 * el desglose de movimientos del día completo, pendientes, tops y alertas.
 * "Variación del día" se mide contra el snapshot INICIAL del día (no el
 * activo, que puede ser intradiario).
 *
 * `comitentesVisibles` acota la agregación a esas cuentas (Dashboard de un
 * comercial: solo sus clientes del SGC). `null` = sin recorte: se agregan todas
 * las cuentas que el proveedor trajo.
 */
export const useSaldosDashboard = (comitentesVisibles = null) => {
    const {
        fechaHoy,
        initialSaldos: initialSaldosTodos,
        allCauciones: allCaucionesTodas,
        allPases: allPasesTodos,
        allOperaciones: allOperacionesTodas,
        instruccionesDinero: instruccionesDineroTodas,
        debitosLicitaciones,
        snapshotsHoy,
        snapshotActivo,
        isLoadingSaldos,
        saldosPorBucket: saldosPorBucketTodos,
        clientesDeudoresSinPrecarga: clientesDeudoresSinPrecargaTodos,
        esModoProductor,
    } = useSaldosClientes();

    const key = scopeKey(comitentesVisibles);
    const scope = useMemo(
        () => (key === null ? null : buildScope(key ? key.split('|') : [])),
        [key],
    );

    const initialSaldos = useMemo(
        () => filterByComitentes(initialSaldosTodos, scope), [initialSaldosTodos, scope]);
    const allCauciones = useMemo(
        () => filterByComitentes(allCaucionesTodas, scope), [allCaucionesTodas, scope]);
    const allPases = useMemo(
        () => filterByComitentes(allPasesTodos, scope), [allPasesTodos, scope]);
    const allOperaciones = useMemo(
        () => filterByComitentes(allOperacionesTodas, scope), [allOperacionesTodas, scope]);
    const instruccionesDinero = useMemo(
        () => filterByComitentes(instruccionesDineroTodas, scope), [instruccionesDineroTodas, scope]);
    const licitaciones = useMemo(
        () => filterByComitentes(debitosLicitaciones.movimientos, scope), [debitosLicitaciones, scope]);
    const saldosPorBucket = useMemo(
        () => MONEDA_BUCKETS.reduce((acc, bucket) => ({
            ...acc,
            [bucket]: filterMapByComitentes(saldosPorBucketTodos[bucket], scope),
        }), {}),
        [saldosPorBucketTodos, scope]);
    const clientesDeudoresSinPrecarga = useCallback(
        (moneda) => filterByComitentes(clientesDeudoresSinPrecargaTodos(moneda), scope),
        [clientesDeudoresSinPrecargaTodos, scope],
    );

    const snapshotInicialHoy = useMemo(
        () => filterSnapshotByComitentes(
            snapshotsHoy.find(s => s.tipo === SNAPSHOT_TIPO.INICIAL) || null,
            scope,
        ),
        [snapshotsHoy, scope],
    );

    const buildCurrencyData = (moneda, map) => {
        const totals = sumSaldosTotales(map);
        const { saldoInicio, saldoInicioEsFallback } =
            sumSnapshotInicio(snapshotInicialHoy, initialSaldos, moneda);
        return {
            saldoInicio,
            saldoInicioEsFallback,
            saldoActual: totals.totalReal,
            saldoProyectado: totals.totalProyectado,
            variacion: totals.totalReal - saldoInicio,
            totalAcreedor: totals.totalAcreedor,
            totalDeudor: totals.totalDeudor,
            desglose: aggregateMovimientosDelDia({
                cauciones: allCauciones,
                pases: allPases,
                operaciones: allOperaciones,
                instrucciones: instruccionesDinero,
                licitaciones,
                moneda,
                fechaHoy,
            }),
            pendientes: { ...totals.pendientes, items: collectPendientesItems(map) },
            topAcreedores: topEntries(map, 1),
            topDeudores: topEntries(map, -1),
            deudoresSinPrecarga: clientesDeudoresSinPrecarga(moneda),
        };
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    const porBucket = useMemo(
        () => MONEDA_BUCKETS.reduce((acc, bucket) => ({
            ...acc,
            [bucket]: buildCurrencyData(bucket, saldosPorBucket[bucket]),
        }), {}),
        [saldosPorBucket, snapshotInicialHoy, initialSaldos, allCauciones, allPases, allOperaciones,
            instruccionesDinero, licitaciones, clientesDeudoresSinPrecarga, fechaHoy],
    );
    /* eslint-enable react-hooks/exhaustive-deps */

    // Con recorte, "hay datos" es sobre las cuentas del alcance: el snapshot del
    // día puede estar cargado y no incluir ninguna de ellas.
    const hasData = scope
        ? MONEDA_BUCKETS.some(b => saldosPorBucket[b].size > 0) || initialSaldos.length > 0
        : !!snapshotActivo || initialSaldosTodos.length > 0;

    return {
        ars: porBucket[BUCKET_ARS],
        usd: porBucket[BUCKET_USD],
        cable: porBucket[BUCKET_CABLE],
        porBucket,
        snapshotInicialHoy,
        snapshotActivo,
        hasData,
        isLoading: isLoadingSaldos,
        esModoProductor,
        hayRecorte: scope !== null,
    };
};
